import React, { useEffect, useState } from "react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Users, Plus, Search, Filter, Building2, Mail, Phone, Edit, Trash2, Eye, Loader, MapPin } from 'lucide-react';
import ClienteForm from "./ClienteForm";
import ClienteDetalleModal from "./ClienteDetalleModal";
import SmartAdaptiveList from "../ui/SmartAdaptiveList";
import ResponsiveModal from "../ui/ResponsiveModal";

export default function ClientesList() {
  const { user } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState({
    tipo: '',
    estado: '',
    ciudad: ''
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [clienteVisualizando, setClienteVisualizando] = useState(null);

  // Función helper para convertir horas decimales a formato HH:MM
  const formatearHoras = (horasDecimal) => {
    if (horasDecimal === null || horasDecimal === undefined || isNaN(horasDecimal)) {
      return '0:00';
    }
    const horas = Math.floor(horasDecimal);
    const minutos = Math.round((horasDecimal - horas) * 60);
    return `${horas}:${minutos.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    cargarClientes();
  }, []);

  async function cargarClientes() {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await http.get("/clientes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(res.data)) {
        // Ordenar alfabéticamente por nombre
        const clientesOrdenados = res.data.sort((a, b) => {
          const nombreA = (a.nombre || "").toLowerCase();
          const nombreB = (b.nombre || "").toLowerCase();
          return nombreA.localeCompare(nombreB);
        });
        setClientes(clientesOrdenados);
        setError(null);
      } else {
        console.error("La respuesta no es un array:", res.data);
        setClientes([]);
        setError("Error: formato de datos inválido");
      }
    } catch (err) {
      console.error("Error cargando clientes", err);
      setClientes([]);
      setError(err.message || "Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  }

  function abrirNuevo() {
    setEditando(null);
    setModalAbierto(true);
  }

  function abrirEditar(cliente) {
    setEditando(cliente);
    setModalAbierto(true);
  }

  async function eliminar(cliente) {
    if (!window.confirm(`¿Eliminar cliente ${cliente.nombre}?`)) return;
    try {
      const token = localStorage.getItem("token");
      const id = cliente?._id || cliente?.id;
      if (!id) {
        console.error("Eliminar cliente: ID no definido en el objeto", cliente);
        alert("No se pudo determinar el ID del cliente. Actualiza la lista y vuelve a intentar.");
        return;
      }
      await http.delete(`/clientes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await cargarClientes();
    } catch (err) {
      console.error("Error eliminando cliente", err);
      const msg = err?.response?.data?.detail || err?.message || "Error al eliminar cliente";
      alert(msg);
    }
  }

  const clientesFiltrados = clientes.filter((c) => {
    const matchBusqueda = !busqueda || 
      c.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
      c.email?.toLowerCase().includes(busqueda.toLowerCase()) ||
      c.telefono?.toLowerCase().includes(busqueda.toLowerCase()) ||
      c.direccion?.toLowerCase().includes(busqueda.toLowerCase()) ||
      c.ruc?.toLowerCase().includes(busqueda.toLowerCase());
    
    const matchTipo = !filtros.tipo || c.tipo === filtros.tipo;
    const matchEstado = !filtros.estado || c.estado === filtros.estado;
    const matchCiudad = !filtros.ciudad || c.ciudad === filtros.ciudad;

    return matchBusqueda && matchTipo && matchEstado && matchCiudad;
  });

  // Obtener valores únicos para filtros
  const tiposCliente = [...new Set(clientes.map(c => c.tipo).filter(Boolean))];
  const estados = [...new Set(clientes.map(c => c.estado).filter(Boolean))];
  const ciudades = [...new Set(clientes.map(c => c.ciudad).filter(Boolean))];

  const abrirVisualizacion = (cliente) => {
    setClienteVisualizando(cliente);
  };

  const cerrarVisualizacion = () => {
    setClienteVisualizando(null);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-emerald-600" />
            Gestión de Clientes
          </h2>
          <p className="text-gray-600">Administra la información de tus clientes y empresas</p>
        </div>
        {(user?.rol === "admin" || user?.rol === "tecnico") && (
          <button
            onClick={abrirNuevo}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors touch-target w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" />
            Nuevo Cliente
          </button>
        )}
      </div>

      {/* Búsqueda y filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-0">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, RUC, email, teléfono o dirección..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors touch-target ${
                mostrarFiltros 
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>

          {/* Filtros avanzados */}
          {mostrarFiltros && (
            <div className="mt-4 pt-4 border-t border-gray-200 mb-0 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Cliente</label>
                <select
                  value={filtros.tipo}
                  onChange={(e) => setFiltros({...filtros, tipo: e.target.value})}
                  className="mobile-input w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Todos los tipos</option>
                  {tiposCliente.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {tipo}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <select
                  value={filtros.estado}
                  onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
                  className="mobile-input w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Todos los estados</option>
                  {estados.map((estado) => (
                    <option key={estado} value={estado}>
                      {estado}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
                <select
                  value={filtros.ciudad}
                  onChange={(e) => setFiltros({...filtros, ciudad: e.target.value})}
                  className="mobile-input w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Todas las ciudades</option>
                  {ciudades.map((ciudad) => (
                    <option key={ciudad} value={ciudad}>
                      {ciudad}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Estadísticas */}
      {clientes.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded border text-center">
            <div className="text-2xl font-bold text-emerald-600">{clientes.length}</div>
            <div className="text-sm text-gray-600">Total Clientes</div>
          </div>
          <div className="bg-white p-4 rounded border text-center">
            <div className="text-2xl font-bold text-green-600">
              {clientes.filter(c => c.estado === 'activo' || !c.estado).length}
            </div>
            <div className="text-sm text-gray-600">Activos</div>
          </div>
          <div className="bg-white p-4 rounded border text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {tiposCliente.length}
            </div>
            <div className="text-sm text-gray-600">Tipos de Cliente</div>
          </div>
          <div className="bg-white p-4 rounded border text-center">
            <div className="text-2xl font-bold text-purple-600">
              {ciudades.length}
            </div>
            <div className="text-sm text-gray-600">Ciudades</div>
          </div>
        </div>
      )}

      {/* Tabla de clientes */}
      <div className="border rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader className="w-8 h-8 animate-spin text-emerald-600" />
            <span className="ml-3 text-gray-600">Cargando clientes...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12 bg-red-50">
            <div className="text-red-600 font-medium mb-2">Error al cargar clientes</div>
            <div className="text-red-500 text-sm">{error}</div>
            <button
              onClick={cargarClientes}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Reintentar
            </button>
          </div>
        ) : clientesFiltrados.length === 0 ? (
          <div className="text-center py-12 bg-gray-50">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron clientes</h3>
            <p className="text-gray-600 mb-4">
              {clientes.length === 0 
                ? "No hay clientes registrados en el sistema."
                : "Intenta ajustar los filtros de búsqueda."
              }
            </p>
            {clientes.length === 0 && (user?.rol === "admin" || user?.rol === "tecnico") && (
              <button
                onClick={abrirNuevo}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                Agregar primer cliente
              </button>
            )}
          </div>
        ) : (
          <SmartAdaptiveList
            items={clientesFiltrados}
            headers={['Cliente', 'Contacto', 'Ubicación', 'Tipo', 'Acciones']}
            renderDesktopRow={(c) => (
              <>
                <td className="px-3 py-3 whitespace-nowrap w-1/4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-emerald-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {c.nombre || 'Sin nombre'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {c.ruc ? `RUC: ${c.ruc}` : 'Sin RUC'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap w-1/5">
                  <div className="text-sm text-gray-900">
                    <div className="flex items-center gap-1 mb-1">
                      <Mail className="w-3 h-3 text-gray-400" />
                      {c.email || 'Sin email'}
                    </div>
                    <div className="flex items-center gap-1">
                      <Phone className="w-3 h-3 text-gray-400" />
                      {c.telefono || 'Sin teléfono'}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap w-1/5">
                  <div className="text-sm text-gray-900">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-gray-400" />
                      <div>
                        <div className="font-medium">{c.ciudad || 'Sin ciudad'}</div>
                        <div className="text-gray-500 text-xs">{c.direccion || 'Sin dirección'}</div>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap w-1/6">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    c.tipo === 'empresa'
                      ? 'bg-purple-100 text-purple-800'
                      : c.tipo === 'gobierno'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {c.tipo || 'Sin tipo'}
                  </span>
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-center w-1/6">
                  <div className="flex justify-center space-x-2">
                    <button
                      className="p-2 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 rounded-lg transition-colors touch-target"
                      onClick={() => abrirVisualizacion(c)}
                      title="Ver detalles"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {(user?.rol === "admin" || user?.rol === "tecnico") && (
                      <button
                        className="p-2 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100 rounded-lg transition-colors touch-target"
                        onClick={() => abrirEditar(c)}
                        title="Editar cliente"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                    {user?.rol === "admin" && (
                      <button
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors touch-target"
                        onClick={() => eliminar(c)}
                        title="Eliminar cliente"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </>
            )}
            renderMobileCard={(c) => (
              <div className="space-y-3">
                {/* Header con nombre y tipo */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center mr-3">
                      <Building2 className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {c.nombre || 'Sin nombre'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {c.ruc ? `RUC: ${c.ruc}` : 'Sin RUC'}
                      </div>
                    </div>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    c.tipo === 'empresa'
                      ? 'bg-purple-100 text-purple-800'
                      : c.tipo === 'gobierno'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {c.tipo || 'Sin tipo'}
                  </span>
                </div>

                {/* Información de contacto */}
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{c.email || 'Sin email'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{c.telefono || 'Sin teléfono'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div className="text-sm text-gray-600">
                      <div className="font-medium">{c.ciudad || 'Sin ciudad'}</div>
                      {c.direccion && <div className="text-xs">{c.direccion}</div>}
                    </div>
                  </div>
                </div>

                {/* Acciones */}
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <button
                    className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors touch-target"
                    onClick={() => abrirVisualizacion(c)}
                  >
                    <Eye className="h-4 w-4" />
                    Ver
                  </button>
                  {(user?.rol === "admin" || user?.rol === "tecnico") && (
                    <button
                      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors touch-target"
                      onClick={() => abrirEditar(c)}
                    >
                      <Edit className="h-4 w-4" />
                      Editar
                    </button>
                  )}
                  {user?.rol === "admin" && (
                    <button
                      className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors touch-target"
                      onClick={() => eliminar(c)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            )}
          />
        )}
        
        {/* Footer con información de resultados */}
        {clientesFiltrados.length > 0 && (
          <div className="bg-gray-50 px-6 py-3 border-t">
            <div className="text-sm text-gray-700">
              Mostrando {clientesFiltrados.length} de {clientes.length} clientes
              {busqueda && ` para "${busqueda}"`}
            </div>
          </div>
        )}
      </div>

      {/* Modal de formulario */}
      <ResponsiveModal
        isOpen={modalAbierto}
        onClose={() => setModalAbierto(false)}
        title={editando ? "Editar Cliente" : "Nuevo Cliente"}
        size="large"
      >
        <ClienteForm
          cliente={editando}
          cerrar={() => setModalAbierto(false)}
          recargar={cargarClientes}
        />
      </ResponsiveModal>

      {/* Modal de visualización con detalles completos */}
      {clienteVisualizando && (
        <ClienteDetalleModal
          cliente={clienteVisualizando}
          onClose={cerrarVisualizacion}
          onEditar={() => {
            cerrarVisualizacion();
            abrirEditar(clienteVisualizando);
          }}
        />
      )}
    </div>
  );
}
